import {
  FC,
  useEffect,
  useState,
  useCallback,
  useRef,
  useReducer,
} from 'react';

import { Box, Typography } from '@mui/material';
import { useMeetingStatus } from 'utils/contexts/meeting-status-context';
import { useNavigate } from 'react-router-dom';
import { INTERPRETER_STATUS_CONNECTED } from '../../utils/constants/interpreterStatus';
import ImageType from 'utils/constants/imageType';
import LoadingSpinner from 'components/LoadingSpinner/loading-spinner';
import SecondaryButton from 'shared/components/button/secondary-button';
import CardAlertContainer from 'shared/components/alert-card/alert-card.component';
import Page from 'components/page/page.component';

const reducer = (state: number, action: { type: string }) => {
  switch (action.type) {
    case 'Increment':
      return state + 1;
    default:
      return state;
  }
};

const LoadingScreen: FC = () => {
  const { getInterpreterStatus, interpreterRequest, resetInterpreterRequest } =
    useMeetingStatus();
  const [interpreterStatus, setInterpreterStatus] = useState('');
  const [showDelayMessage, setShowDelayMessage] = useState(false);
  const statusInterval = useRef<NodeJS.Timeout>();
  const [attempts, dispatch] = useReducer(reducer, 0);
  const [timeOut, setTimeout] = useState(false);
  const navigate = useNavigate();

  const { callId } = interpreterRequest;

  const checkInterpreterStatus = useCallback(async () => {
    dispatch({ type: 'Increment' });
    const status = await getInterpreterStatus(callId);

    setInterpreterStatus(status);
  }, [callId, getInterpreterStatus]);

  const handleCancel = () => {
    if (statusInterval.current) {
      clearInterval(statusInterval.current);
    }
    setShowDelayMessage(false);
  };

  useEffect(() => {
    if (!callId) {
      navigate('/');
    }
  }, [callId, navigate]);

  useEffect(() => {
    if (!statusInterval.current) {
      statusInterval.current = setInterval(checkInterpreterStatus, 5000);
    }
    return () => {
      handleCancel();
    };
  }, [checkInterpreterStatus]);

  useEffect(() => {
    if (attempts > 9) {
      setShowDelayMessage(true);
    }
    if (attempts > 60) {
      setTimeout(true);
      handleCancel();
    }
  }, [attempts]);

  useEffect(() => {
    if (interpreterStatus === INTERPRETER_STATUS_CONNECTED) {
      navigate('/thankyou');
    }
  }, [interpreterStatus, navigate]);

  return (
    <Page
      title='Thank you for choosing Sorenson for Zoom'
      imageOption={!timeOut ? ImageType.Success : ImageType.Ready}
      pageTitle='Getting Interpreter'
    >
      {!timeOut ? (
        <>
          <Box
            id='loading-screen-text'
            sx={{
              display: 'grid',
              textAlign: 'center',
              width: '80%',
            }}
          >
            <Typography
              sx={{ color: 'secondary.contrastText', marginBottom: '25px' }}
            >
              An interpreter has been requested for your meeting
            </Typography>
          </Box>
          <LoadingSpinner />
          <Typography
            id='loading-screen-getting-interpreter'
            sx={{
              fontSize: '20px',
              color: 'text.secondary',
              margin: '3px 0 32px 0',
            }}
          >
            Getting an interpreter...
          </Typography>
          {/*TODO implement dynamic messages */}
          {showDelayMessage && (
            <Box
              id='loading-screen-delay-message'
              sx={{
                display: 'grid',
                textAlign: 'center',
                width: '80%',
              }}
            >
              <Typography sx={{ fontWeight: '400', color: 'error.main' }}>
                Wait times might be longer than normal; we’re sorry for any
                delay.
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <>
          <CardAlertContainer
            severity='error'
            mainText='Sorry, but the interpreter request timed out.'
          />
          <SecondaryButton
            id='request-interpreter-again-btn'
            onClick={resetInterpreterRequest}
            sx={{
              textTransform: 'unset !important',
            }}
          >
            Request an interpreter again
          </SecondaryButton>
        </>
      )}
    </Page>
  );
};

export default LoadingScreen;
